import React, { useState, useEffect, useCallback } from 'react';
import './SubscribePopup.css';

const CHANNEL_URL   = 'https://youtube.com/@techbywebcoder?si=Jucl9rsD0rdwv81c';
const RESHOW_DELAY  = 300_000; // 300 seconds = 5 minutes
const OPEN_DELAY    = 1_000;   // 1 second after page load → feels natural, not jarring

const SubscribePopup = () => {
  const [visible, setVisible] = useState(false);

  /* ── Schedule the popup to open ── */
  const scheduleOpen = useCallback((delay = OPEN_DELAY) => {
    const timer = setTimeout(() => setVisible(true), delay);
    return timer;
  }, []);

  /* ── On mount: show after 1s, then every 300s after each close ── */
  useEffect(() => {
    const timer = scheduleOpen(OPEN_DELAY);
    return () => clearTimeout(timer);
  }, [scheduleOpen]);

  /* ── Lock body scroll while popup is open ── */
  useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [visible]);

  /* ── Close on Escape key ── */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    if (visible) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  /* Close → hide, then re-open after 300 s */
  const handleClose = useCallback(() => {
    setVisible(false);
    const timer = setTimeout(() => setVisible(true), RESHOW_DELAY);
    // Clean up if the component unmounts before the timer fires
    return () => clearTimeout(timer);
  }, []);

  /* Subscribe → open channel tab, then re-open popup after 300 s */
  const handleSubscribe = useCallback(() => {
    window.open(CHANNEL_URL, '_blank', 'noopener,noreferrer');
    setVisible(false);
    setTimeout(() => setVisible(true), RESHOW_DELAY);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="sp-backdrop animate-fadeIn"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Subscribe to YouTube channel"
    >
      <div className="sp-card animate-scaleIn">

        {/* Close button */}

        {/* Decorative blobs */}
        <div className="sp-blob sp-blob-1" aria-hidden="true" />
        <div className="sp-blob sp-blob-2" aria-hidden="true" />

        {/* YouTube icon */}
        <div className="sp-yt-icon-wrap" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor" className="sp-yt-svg">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </div>

        {/* Channel name badge */}
        <span className="sp-channel-badge">@techbywebcoder</span>

        {/* Headline */}
        <h2 className="sp-title">
          Enjoying the<br />
          <span className="sp-title-highlight">Content?</span>
        </h2>

        <p className="sp-subtitle">
          Support the channel — it's <strong>free</strong> and helps us
          keep making awesome tutorials for you!
        </p>

        {/* Subscribe button */}
        <button className="sp-subscribe-btn" onClick={handleSubscribe}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
          Subscribe Now
        </button>

        {/* Dismiss link */}
        <button className="sp-dismiss" onClick={handleClose}>
          Maybe later
        </button>

      </div>
    </div>
  );
};

export default SubscribePopup;